// packages/renderer/src/db/edb/folder-index.ts
//
// What the connected workspace folder holds, and how it merges with the database
// this tab has open.
//
// The problem it solves: a workspace's data lives in a file, but the workspace
// LIST is a table inside one file, and a tab can only hold one database at a time
// (the pool's files are exclusive origin-wide — see `tab-lock.ts`). So the only
// place that can say "the `sales` workspace lives in `sales.edb`" is outside every
// database.
//
// Device-local, and deliberately a CACHE rather than a record: it is rebuilt from
// the folder by `folder-sync.ts`, so a stale entry costs one failed lookup and
// heals on the next scan. Nothing here is authoritative, which is why no schema
// changed to make room for it and why a `.edb` handed to someone else carries none
// of it.

import { spaceFileName, type WorkspaceContents } from '@easydb/shared';
import { deviceStore } from '../user-settings.js';
import { FOLDER_INDEX_KEY, FOLDER_SELECTION_KEY } from './device-keys.js';

/** One workspace found in one file in the folder. */
export interface FolderWorkspace {
  id: string;
  title?: string | undefined;
  /** The `.edb` in the connected folder that holds it. */
  file: string;
  /**
   * What the copy IN THE FILE holds, and what the file itself looks like.
   *
   * All optional: an index written by an older version has none of it, and this
   * is a cache nothing may depend on. They exist for the conflict prompts, which
   * cannot ask "which copy do you want" usefully without them — `size` and
   * `mtime` belong to the FILE, so every workspace in one file repeats them.
   */
  tables?: number | undefined;
  views?: number | undefined;
  size?: number | undefined;
  mtime?: number | undefined;
}

export interface FolderIndex {
  /** The folder's display name, for the selector and the sync report. */
  folder: string;
  /** When the scan ran, so the UI can say how old this is. */
  at: number;
  workspaces: FolderWorkspace[];
  /**
   * Every `.edb` NAME the last scan saw, including the ones it did not read.
   *
   * Separate from `workspaces` because a file the user has switched off is
   * never opened — so it has no workspace entry — and the Local Data dialog
   * still has to list it, or there would be no way to switch it back on.
   * Listing a directory is cheap; reading and parsing each file is not, and
   * that is the whole saving.
   *
   * Optional: an index written before this existed has none, and the dialog
   * falls back to the files its workspaces name.
   */
  files?: string[] | undefined;
}

/**
 * Through the DEVICE layer, not `localStorage` directly.
 *
 * `deviceStore()` is this browser until a folder is connected and the folder's
 * `_easydb.edp` after that (`device-state.ts`), and the index is one of the keys
 * the folder owns. That is what puts the workspace list in the folder's own
 * metadata rather than in one browser — the whole point of it not living inside a
 * `.edb`. Writes still reach `localStorage` as well, so the selector can paint
 * before a folder has been adopted.
 */
export function readFolderIndex(): FolderIndex | null {
  try {
    const raw = deviceStore()?.getItem(FOLDER_INDEX_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FolderIndex;
    // A shape check, not a schema: this is a cache, and a version of the app that
    // wrote it differently is better ignored than crashed on.
    return Array.isArray(parsed?.workspaces) ? parsed : null;
  } catch {
    return null; // private mode, or a value from another version
  }
}

export function writeFolderIndex(index: FolderIndex): void {
  try {
    deviceStore()?.setItem(FOLDER_INDEX_KEY, JSON.stringify(index));
  } catch {
    /* private mode, or out of room — the app works, the list is just not merged */
  }
}

export function clearFolderIndex(): void {
  try {
    deviceStore()?.removeItem(FOLDER_INDEX_KEY);
  } catch {
    /* nothing to clear that anything can reach */
  }
}

// -- which files in the folder are in play ----------------------------------

/**
 * Which `.edb` files of the connected folder this device uses.
 *
 * `all` is a RULE, not a tick-everything shortcut: with it on, a file dropped
 * into the folder tomorrow is picked up without anyone revisiting this. That is
 * why it is not simply "every name is in `files`" — the two answer differently
 * the moment the folder changes, and "all" is the one that keeps working.
 *
 * `files` is only read when `all` is off, and it is a list of file NAMES, which
 * is what the folder deals in everywhere else.
 *
 * Device-local, like the index beside it, and for a stronger reason: it says
 * what THIS machine wants to look at. Carried inside a shared `.edb` it would
 * hide someone else's workspaces on their own computer.
 */
export interface FolderSelection {
  all: boolean;
  files: string[];
}

const SELECTION_KEY = FOLDER_SELECTION_KEY;

/** The default: everything, which is exactly how the folder behaved before this existed. */
export const ALL_FILES: FolderSelection = { all: true, files: [] };

export function readFolderSelection(): FolderSelection {
  try {
    const raw = globalThis.localStorage?.getItem(SELECTION_KEY);
    if (!raw) return ALL_FILES;
    const parsed = JSON.parse(raw) as Partial<FolderSelection>;
    // A shape check, not a schema — same rule as the index above. Anything
    // unrecognisable means "all", never "nothing": a bad value must not make a
    // user's workspaces disappear.
    if (typeof parsed?.all !== 'boolean') return ALL_FILES;
    return { all: parsed.all, files: Array.isArray(parsed.files) ? parsed.files.filter((f) => typeof f === 'string') : [] };
  } catch {
    return ALL_FILES;
  }
}

export function writeFolderSelection(selection: FolderSelection): void {
  try {
    globalThis.localStorage?.setItem(SELECTION_KEY, JSON.stringify(selection));
  } catch {
    /* private mode — the app works, it just forgets the choice next load */
  }
}

/**
 * May this file be read, listed and written?
 *
 * The file this tab currently has OPEN is always yes, whatever the selection
 * says. Its workspace is live — the store, the panels and every plugin are
 * bound to it — so "skip it" is not a state the app can be in. The dialog shows
 * that row ticked and disabled rather than pretending otherwise.
 */
export function fileActive(file: string, selection: FolderSelection, activeFile: string): boolean {
  if (file === activeFile) return true;
  return selection.all || selection.files.includes(file);
}

/** The subset of a scan the selection admits. Pure, so the rule is testable on its own. */
export function activeFiles(files: readonly string[], selection: FolderSelection, activeFile: string): string[] {
  return files.filter((f) => fileActive(f, selection, activeFile));
}

/**
 * Drop indexed workspaces whose file is switched off.
 *
 * A scan already skips those files, so this is normally a no-op. It matters for
 * the index written BEFORE a file was switched off: without it the selector
 * would keep offering that workspace until the next scan, and picking it would
 * adopt a file the user just said to leave alone.
 */
export function activeWorkspaces(indexed: readonly FolderWorkspace[], selection: FolderSelection, activeFile: string): FolderWorkspace[] {
  return indexed.filter((w) => fileActive(w.file, selection, activeFile));
}

/** A workspace as the selector needs to show it. */
export interface ListEntry {
  id: string;
  /** The display title, when the workspace has one. See {@link workspaceLabel}. */
  title?: string | undefined;
  /**
   * The file holding it, when that is NOT this tab's database.
   *
   * Present is what makes an entry a SWITCH rather than a selection: picking it
   * goes through `?space=`, which adopts the file and reloads.
   */
  file?: string | undefined;
}

/**
 * What to CALL a workspace on screen.
 *
 * `title` is what the user calls it and `id` is the technical name `?space=`
 * routes on (see `types.ts`), so everything a user reads goes through here — the
 * selector showed the technical one and went on showing it after a title edit,
 * which reads as the edit not having taken. A blank title is no title, matching
 * the header.
 *
 * The fallback is the ID, which is also the file name. It used to be `name`, a
 * third identifier that was minted from the same slug and then free to drift
 * from both.
 */
export function workspaceLabel(w: { id: string; title?: string | undefined }): string {
  return w.title?.trim() || w.id;
}

const byLabel = (a: ListEntry, b: ListEntry) => workspaceLabel(a).localeCompare(workspaceLabel(b));

/**
 * What each entry should READ as, qualified where the list could not otherwise
 * tell two rows apart.
 *
 * Two entries carrying one label is normal and fine when they live in different
 * files: the file name is the tooltip, and that is what the user is asking when
 * they hover. It is NOT fine inside one file, because then the tooltip says the
 * same thing too and the two rows are identical — one workspace file holding two
 * workspaces breaks the rule (`one-per-file.ts`), and while it is broken the list
 * still has to be readable. The reported case: two rows both saying "PowerPlants",
 * both in `powerplants.edb`, and no way to tell which is which.
 *
 * The qualifier is the ID, which is what `?space=` routes on, what the delete
 * prompt quotes and what the file is named after — so a qualified row reads the
 * same way as the question that comes next. It is also the last thing two
 * workspaces in one database cannot share: it is their primary key.
 */
export function listLabels(entries: readonly ListEntry[]): string[] {
  // Grouped by what a user can see: the label plus the file it lives in. An entry
  // with no file is in the database this tab has open, which is one place, so they
  // all fall in one group.
  const key = (e: ListEntry) => `${workspaceLabel(e)} ${(e.file ?? '').toLowerCase()}`;
  const groups = new Map<string, ListEntry[]>();
  for (const e of entries) {
    const list = groups.get(key(e));
    if (list) list.push(e);
    else groups.set(key(e), [e]);
  }
  return entries.map((e) => {
    const group = groups.get(key(e)) ?? [e];
    if (group.length < 2) return workspaceLabel(e);
    // A label that is already the id gains nothing from repeating it: that is an
    // entry with no title, whose label IS what the qualifier would say.
    return workspaceLabel(e) === e.id ? e.id : `${workspaceLabel(e)} (${e.id})`;
  });
}

/**
 * Deduplicated on (id, file), because two scans of one folder must not double
 * the list.
 *
 * It used to drop the file this tab has OPEN, on the grounds that its workspaces
 * arrived through the open database instead. Nothing arrives that way any more —
 * the open database is not a list source — so the open file's entry is the only
 * one there is, and dropping it took the workspace the user is looking at off
 * their own list.
 */
function deduped(indexed: FolderWorkspace[]): FolderWorkspace[] {
  const seen = new Set<string>();
  const out: FolderWorkspace[] = [];
  for (const w of indexed) {
    const key = `${w.id} ${w.file}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(w);
  }
  return out.sort(byLabel);
}

/**
 * What the workspace selector should list. **One row per workspace id.**
 *
 * Two sources, and NEITHER of them is the database this tab has open. That is the
 * whole fix: the open database changes when the user switches, so anything read
 * out of it made the list a property of where they were standing rather than of
 * their setup.
 *
 * 1. `indexed` — what the last folder scan found, each entry carrying its file.
 *    First because it is the freshest and it is authoritative about files.
 * 2. `registered` — what each DATABASE was last seen holding
 *    (`space-registry.ts`), which is the only thing that can answer for the
 *    project index while a tab has a `.edb` open.
 *
 * A workspace used to be listed TWICE when this browser and a file each held a
 * copy, so that declining the conflict prompt left both reachable. The id is the
 * identity now — it is also the file name — and boot settles which copy to open
 * (`space-resolve.ts`). A list that says the same thing twice is one nobody can
 * read.
 */
export function mergeWorkspaceList(indexed: readonly FolderWorkspace[], registered: readonly ListEntry[] = []): ListEntry[] {
  // Case-insensitive, like every other comparison in this layer: two ids differing
  // only in case are one file on Windows, so they are one workspace to the list too.
  const seen = new Set<string>();
  const out: ListEntry[] = [];
  const take = (e: ListEntry): void => {
    if (seen.has(idKey(e.id))) return;
    seen.add(idKey(e.id));
    out.push(e);
  };
  // `title` is omitted rather than set to undefined when there is none:
  // `exactOptionalPropertyTypes` is on, and an absent key is what callers compare
  // against.
  for (const w of canonicalFirst(deduped([...indexed]))) take({ id: w.id, ...(w.title === undefined ? {} : { title: w.title }), file: w.file });
  for (const e of registered) take(e);
  return out.sort(byLabel);
}

/**
 * Which of two files holding ONE workspace the single row should stand for.
 *
 * A folder can hold `simon.edb` and `powerplants.edb` and both hold the workspace
 * `simon` — a file duplicated in a file manager, a sync tool's second copy, an OS
 * save dialog that renamed one. One row per id means one of them has to win, and
 * leaving it to scan order would make the answer change between two scans of the
 * same folder.
 *
 * The file NAMED after the workspace wins, because that is the one everything else
 * already agrees on: Save writes it, Open reads the id back out of it, and
 * `settleIdentities` offers to rename the other to match its own contents. The
 * odd one out is not lost — the sync report names it, and repairing it is what
 * puts it back on the list under the id its name claims.
 */
function canonicalFirst(indexed: readonly FolderWorkspace[]): FolderWorkspace[] {
  const rank = (w: FolderWorkspace) => (w.file.toLowerCase() === spaceFileName(w.id).toLowerCase() ? 0 : 1);
  return [...indexed].sort((a, b) => rank(a) - rank(b));
}

/**
 * One workspace that exists on both sides: here, and in a file in the folder.
 *
 * ONE id, on `file.id`. This used to carry two — the local one and the file's —
 * because a clash was matched on `name`, a third identifier that could pair two
 * workspaces whose ids differed. With `name` gone the id is the identity on both
 * sides, so a second one would be the same value under another label: exactly the
 * kind of duplicate that made this bug.
 */
export interface FolderClash {
  /** The copy in the folder, as the scan found it. */
  file: FolderWorkspace;
}

/** The id two copies are matched by. Case-insensitive, like the file names. */
function idKey(id: string): string {
  return id.trim().toLowerCase();
}

/**
 * The workspaces a scan found in the folder that the open database ALSO has.
 *
 * This is what gets a prompt, one per workspace: two copies of one workspace
 * exist and only the user knows which is the real one.
 *
 * Matched on the ID, which is now the only thing a workspace is identified by.
 *
 * It used to match on `name`, a third identifier minted from the same slug as the
 * id and then free to drift from it — so "the same workspace" was a question with
 * two answers that disagreed the moment anything was renamed. With `name` gone
 * the question has one answer, and it is the one the file name also gives.
 *
 * Case-insensitive, like every other comparison in this layer: two ids differing
 * only in case are one file on Windows.
 */
export function folderConflicts(open: readonly { id: string }[], indexed: readonly FolderWorkspace[], activeFile: string): FolderClash[] {
  const here = new Map<string, string>();
  for (const w of open) if (!here.has(idKey(w.id))) here.set(idKey(w.id), w.id);
  const out: FolderClash[] = [];
  for (const w of deduped([...indexed].filter((w) => w.file !== activeFile))) {
    const localId = here.get(idKey(w.id));
    if (localId) out.push({ file: w });
  }
  return out;
}

/**
 * Would keeping `keep` and dropping `replace` throw away the only copy of some
 * work?
 *
 * True only when one side holds nothing and the other holds something. A count
 * nobody could take is `undefined` and answers false — an absent count is not a
 * count of none, the same rule `copy-facts.ts` renders by. So this never invents
 * a warning out of missing information, which would train the user to click past
 * the one that matters.
 */
export function overwriteLosesData(keep: { tables?: number | undefined; views?: number | undefined }, replace: { tables?: number | undefined; views?: number | undefined }): boolean {
  const holds = (c: { tables?: number | undefined; views?: number | undefined }): boolean | undefined => {
    if (c.tables === undefined && c.views === undefined) return undefined;
    return (c.tables ?? 0) > 0 || (c.views ?? 0) > 0;
  };
  return holds(keep) === false && holds(replace) === true;
}

/**
 * Whether a workspace holds nothing the user put there.
 *
 * Tables and view instances only. A workspace created a moment ago already has
 * seeded view templates and settings rows — 4 and 8 of them on v0.0.396 — so
 * counting those would make every workspace look used and this would never be
 * true of anything.
 */
export function isEmptyWorkspace(c: WorkspaceContents): boolean {
  return c.tables === 0 && c.views === 0;
}

/**
 * Split the conflicts into the ones that answer themselves and the ones only the
 * user can answer.
 *
 * A conflict is worth a prompt when both sides hold work, which is the case the
 * prompt was written for. It is NOT the case when the local side is an empty
 * shell, and the app makes those itself: `?space=simon` creates an empty `simon`
 * whenever it cannot find that workspace at boot, and at boot it cannot look in
 * a folder nobody has connected yet (see `space-resolve.ts`). A private window
 * hits this every time — no local database, no remembered folder, so the
 * requested workspace is created, and connecting the folder a moment later then
 * asked which of the two `simon`s was real when one of them was seconds old and
 * empty.
 *
 * An empty local side has nothing to lose, so the file wins with no question —
 * the same reasoning `decideSpace` applies to `hasLocalDb`.
 */
export function partitionConflicts(clashes: readonly FolderClash[], emptyLocally: ReadonlySet<string>): { adopt: FolderClash[]; ask: FolderClash[] } {
  const adopt: FolderClash[] = [];
  const ask: FolderClash[] = [];
  for (const c of clashes) (emptyLocally.has(c.file.id) ? adopt : ask).push(c);
  return { adopt, ask };
}
